// Shared refetch wiring for the tab screens (Main, Contacts). Reloads on:
//  - mount and whenever a friend.* WS frame bumps the event sequence;
//  - the moment signaling (auth + WS) reaches 'connected' — on a cold start the
//    mount fetch races the background session resume and loses (it runs before
//    tokens exist), and after any outage reconnection must refresh without user
//    action (offline-first: recovery is automatic, docs/06 §5).

import { useEffect, useRef } from 'react';
import { useScreens } from '../ScreensContext.js';
import { useStore } from './useStore.js';

export function useServerReload(reload: () => Promise<void>): void {
  const { engine, liveEvents } = useScreens();
  const engineState = useStore(engine.state);
  const live = useStore(liveEvents.state);

  useEffect(() => {
    void reload();
  }, [reload, live.friendEventSeq]);

  // Edge-triggered on the disconnected→connected TRANSITION only. Reloading
  // whenever connected-at-mount doubled every screen's fetch burst (the mount
  // effect above already covers mount — field report: duplicated requests in
  // devtools). Seeded true so a mount that is already connected records the
  // level without firing a second reload.
  const signalingConnected = engineState.signaling === 'connected';
  const prevConnected = useRef(true);
  useEffect(() => {
    const wasConnected = prevConnected.current;
    prevConnected.current = signalingConnected;
    if (signalingConnected && !wasConnected) void reload();
  }, [signalingConnected, reload]);
}
